import React,{useState,useEffect}from"react";
import{Shield,Bell,RefreshCw,UserPlus,AlertCircle,Check,Save,Info,Cloud,Download,UserMinus,FolderOpen,Building2,Tag,Plus,FlaskConical,ArrowRight,Eye,Bug,Lightbulb,HelpCircle,ExternalLink,ChevronDown,ChevronUp,History,Send,Mail,Loader}from"lucide-react";
import{BLUE,parseTeamsGroupName,INTERNAL_CATEGORIES,dispName}from"../config";
import{RoleBadge,Inp,Sel,Spin}from"./UI";
import * as Graph from"../Graph";

const DIGEST_API_URL="https://taskhub-functions-dfbwdzdgbbdfgffj.centralus-01.azurewebsites.net/api/dailyDigest";

function DigestTester({cu}){
  const[running,setRunning]=useState(false);
  const[result,setResult]=useState(null);
  const[error,setError]=useState("");
  const[mode,setMode]=useState("mine"); // "mine" = only my digest, "all" = all users to my email

  const runDigest=async()=>{
    setRunning(true);setResult(null);setError("");
    try{
      const params=new URLSearchParams();
      params.set("sendTo",cu.email);
      if(mode==="mine") params.set("onlyUser",cu.email);
      const res=await fetch(`${DIGEST_API_URL}?${params.toString()}`);
      const data=await res.json();
      if(!res.ok) throw new Error(data.error||"Request failed");
      setResult(data);
    }catch(e){setError("Digest test failed: "+e.message);}
    finally{setRunning(false);}
  };

  return(
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100">
        <h3 className="text-sm font-semibold flex items-center gap-2"><Mail size={14} style={{color:BLUE}}/>Test Daily Digest</h3>
        <p className="text-xs text-slate-400 mt-1">Send debug digest emails to <strong>{cu.email}</strong>. Users with no tasks are skipped.</p>
      </div>
      <div className="px-5 py-4 space-y-3">
        <div className="flex gap-2">
          <button onClick={()=>setMode("mine")} className={"text-xs px-3 py-1.5 rounded-full border font-medium "+(mode==="mine"?"text-white border-transparent":"text-slate-500 border-slate-200 hover:bg-slate-50")} style={mode==="mine"?{background:BLUE}:{}}>My Digest Only</button>
          <button onClick={()=>setMode("all")} className={"text-xs px-3 py-1.5 rounded-full border font-medium "+(mode==="all"?"text-white border-transparent":"text-slate-500 border-slate-200 hover:bg-slate-50")} style={mode==="all"?{background:BLUE}:{}}>All Users (to my email)</button>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={runDigest} disabled={running} className="flex items-center gap-2 px-4 py-2 text-sm text-white font-semibold rounded-lg disabled:opacity-50" style={{background:BLUE}}>
            {running?<Loader size={14} className="animate-spin"/>:<Send size={14}/>}
            {running?"Sending...":"Send Test Digest"}
          </button>
          {result&&!error&&(
            <span className="text-sm text-emerald-600 flex items-center gap-1"><Check size={14}/>{result.sent} email{result.sent!==1?"s":""} sent to {cu.email}</span>
          )}
          {error&&<span className="text-sm text-red-600 flex items-center gap-1"><AlertCircle size={14}/>{error}</span>}
        </div>
      </div>
      {result?.users?.length>0&&(
        <div className="px-5 pb-4">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-2">Emails Sent</p>
          <div className="space-y-1">
            {result.users.map((u,i)=>(
              <div key={i} className="flex items-center justify-between text-xs bg-slate-50 rounded-lg px-3 py-2">
                <span className="font-medium text-slate-700">{u.name}</span>
                <span className="text-slate-400">{u.tasks} task{u.tasks!==1?"s":""}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function NotifSettings({cu,getToken}){
  const DEFAULTS={assignment_notify_enabled:"true",reminder_lead_days:"2",reminder_frequency:"once",overdue_alerts_enabled:"true",overdue_alert_frequency:"daily",digest_enabled:"true",digest_send_time:"07:00",digest_days:"Mon,Tue,Wed,Thu,Fri"};
  const[settings,setSettings]=useState(DEFAULTS);
  const[spIds,setSpIds]=useState({});
  const[loading,setLoading]=useState(true);
  const[saving,setSaving]=useState(false);
  const[error,setError]=useState("");
  const[success,setSuccess]=useState("");

  const load=async()=>{
    setLoading(true);setError("");
    try{
      const tok=await getToken();
      const map=await Graph.getSettings(tok);
      const merged={...DEFAULTS};const ids={};
      Object.entries(map).forEach(([k,{value,spId}])=>{merged[k]=value;ids[k]=spId;});
      setSettings(merged);setSpIds(ids);
    }catch(e){setError("Failed to load settings: "+e.message);}
    finally{setLoading(false);}
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(()=>{load();},[]);

  const upd=(k,v)=>setSettings(p=>({...p,[k]:v}));
  const save=async()=>{
    setSaving(true);setError("");
    try{
      const tok=await getToken();
      await Promise.all(Object.entries(settings).map(([k,v])=>Graph.saveSetting(tok,k,v,spIds[k],cu.email)));
      setSuccess("Settings saved.");setTimeout(()=>setSuccess(""),3000);
    }catch(e){setError("Save failed: "+e.message);}
    finally{setSaving(false);}
  };

  const Row=({label,children})=><div className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0"><span className="text-sm text-slate-600 w-48 shrink-0">{label}</span><div className="flex-1 flex justify-end">{children}</div></div>;
  const Toggle=({sk})=><button onClick={()=>upd(sk,settings[sk]==="true"?"false":"true")} className={"relative inline-flex h-6 w-11 rounded-full transition-colors "+(settings[sk]==="true"?"bg-blue-600":"bg-slate-200")}><span className={"inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform mt-0.5 "+(settings[sk]==="true"?"translate-x-5":"translate-x-0.5")}/></button>;

  if(loading)return<div className="flex items-center justify-center py-16"><Spin/></div>;
  return(
    <div className="max-w-xl space-y-5">
      <div className="flex items-center justify-between">
        <div><h2 className="text-base font-semibold flex items-center gap-2"><Bell size={16} style={{color:BLUE}}/>Notification Settings</h2><p className="text-xs text-slate-400 mt-0.5">Global defaults for Power Automate email flows</p></div>
        <button onClick={load} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-600"><RefreshCw size={11}/>Refresh</button>
      </div>
      {error&&<div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 flex items-center gap-2"><AlertCircle size={14}/>{error}</div>}
      {success&&<div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-sm text-emerald-700 flex items-center gap-2"><Check size={14}/>{success}</div>}
      <div className="bg-white border border-slate-200 rounded-xl px-5 py-1">
        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest pt-4 pb-2">Task Reminders</p>
        <Row label="Assignment notifications"><Toggle sk="assignment_notify_enabled"/></Row>
        <Row label="Reminder lead time (days)"><input type="number" min="1" max="14" value={settings.reminder_lead_days} onChange={e=>upd("reminder_lead_days",e.target.value)} className="w-20 border border-slate-200 rounded-lg px-3 py-1.5 text-sm text-right focus:outline-none"/></Row>
        <Row label="Reminder frequency">
          <select value={settings.reminder_frequency} onChange={e=>upd("reminder_frequency",e.target.value)} className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none">
            <option value="once">Once</option><option value="daily">Daily</option><option value="every2">Every 2 days</option><option value="every3">Every 3 days</option>
          </select>
        </Row>
        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest pt-4 pb-2">Overdue Alerts</p>
        <Row label="Overdue alerts enabled"><Toggle sk="overdue_alerts_enabled"/></Row>
        <Row label="Alert frequency">
          <select value={settings.overdue_alert_frequency} onChange={e=>upd("overdue_alert_frequency",e.target.value)} className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none">
            <option value="daily">Daily</option><option value="every2">Every 2 days</option><option value="weekly">Weekly</option>
          </select>
        </Row>
        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest pt-4 pb-2">Manager Daily Digest</p>
        <Row label="Digest enabled"><Toggle sk="digest_enabled"/></Row>
        <Row label="Send time"><input type="time" value={settings.digest_send_time} onChange={e=>upd("digest_send_time",e.target.value)} className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none"/></Row>
        <Row label="Days of week">
          <div className="flex gap-1">
            {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map(d=>{const active=settings.digest_days.includes(d);return<button key={d} onClick={()=>{const days=settings.digest_days.split(",").filter(Boolean);const next=active?days.filter(x=>x!==d):[...days,d];upd("digest_days",next.join(","));}} className={"px-2 py-1 rounded text-xs font-medium border "+(active?"text-white border-transparent":"text-slate-500 border-slate-200 hover:bg-slate-50")} style={active?{background:BLUE}:{}}>{d}</button>;})}
          </div>
        </Row>
      </div>
      <button onClick={save} disabled={saving} className="flex items-center gap-2 px-5 py-2.5 text-sm text-white font-semibold rounded-xl disabled:opacity-50" style={{background:BLUE}}>{saving&&<Spin/>}<Save size={14}/>Save Settings</button>
      <DigestTester cu={cu}/>
    </div>
  );
}

function ProjectsAdmin({cu,getToken,projects,setProjects}){
  const[loading,setLoading]=useState(true);
  const[syncing,setSyncing]=useState(false);
  const[saving,setSaving]=useState(null);
  const[error,setError]=useState("");
  const[success,setSuccess]=useState("");
  const[syncMatches,setSyncMatches]=useState(null);
  const[addOpen,setAddOpen]=useState(false);
  const[newForm,setNewForm]=useState({customerName:"",jobNumber:"",projectName:"",projectType:"customer",isQuote:false});
  const[editingProj,setEditingProj]=useState(null); // spId of row being edited
  const[editProjForm,setEditProjForm]=useState({});

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(()=>{loadProjects();},[]);

  const loadProjects=async()=>{
    setLoading(true);setError("");
    try{
      const tok=await getToken();
      const projs=await Graph.getProjects(tok);
      // Auto-populate internal categories if none exist
      const hasInternal=projs.some(p=>p.projectType==="internal");
      if(!hasInternal){
        const created=[];
        for(const cat of INTERNAL_CATEGORIES){
          const p=await Graph.upsertProject(tok,{projectName:cat.projectName,projectType:"internal",isActive:true,isQuote:false,customerName:"",jobNumber:"",teamsGroupId:"",teamsGroupName:""});
          created.push(p);
        }
        projs.push(...created);
        setSuccess("Internal categories created automatically.");setTimeout(()=>setSuccess(""),4000);
      }
      setProjects(projs);
    }catch(e){setError("Failed to load projects: "+e.message);}
    finally{setLoading(false);}
  };

  const toggleActive=async(p)=>{
    setSaving(p.spId);
    try{
      const tok=await getToken();
      const updated={...p,isActive:!p.isActive};
      await Graph.upsertProject(tok,updated);
      setProjects(ps=>ps.map(x=>x.spId===p.spId?updated:x));
    }catch(e){setError("Update failed: "+e.message);}
    finally{setSaving(null);}
  };

  const toggleQuote=async(p)=>{
    setSaving(p.spId+"-q");
    try{
      const tok=await getToken();
      const updated={...p,isQuote:!p.isQuote};
      await Graph.upsertProject(tok,updated);
      setProjects(ps=>ps.map(x=>x.spId===p.spId?updated:x));
    }catch(e){setError("Update failed: "+e.message);}
    finally{setSaving(null);}
  };

  const syncTeams=async()=>{
    setSyncing(true);setError("");setSyncMatches(null);
    try{
      const groups=await Graph.getTeamsGroups();
      const matches=[];
      for(const g of groups){
        const parsed=parseTeamsGroupName(g.displayName);
        if(!parsed)continue;
        const already=projects.find(p=>p.teamsGroupId===g.id||p.jobNumber===parsed.jobNumber);
        matches.push({...parsed,teamsGroupId:g.id,teamsGroupName:g.displayName,already:!!already,existingProject:already});
      }
      setSyncMatches(matches);
    }catch(e){setError("Sync failed: "+e.message);}
    finally{setSyncing(false);}
  };

  const importProject=async(match)=>{
    setSaving(match.teamsGroupId);
    try{
      const tok=await getToken();
      const p=await Graph.upsertProject(tok,{customerName:match.customerName,jobNumber:match.jobNumber,projectName:match.projectName,projectType:"customer",isQuote:false,isActive:true,teamsGroupId:match.teamsGroupId,teamsGroupName:match.teamsGroupName});
      setProjects(ps=>[...ps,p]);
      setSyncMatches(m=>m.map(x=>x.teamsGroupId===match.teamsGroupId?{...x,already:true,existingProject:p}:x));
      setSuccess("Project imported.");setTimeout(()=>setSuccess(""),3000);
    }catch(e){setError("Import failed: "+e.message);}
    finally{setSaving(null);}
  };

  const addProject=async()=>{
    if(!newForm.projectName.trim()){setError("Project name is required.");return;}
    if(newForm.projectType==="customer"&&!newForm.jobNumber.trim()){setError("Job number is required for customer projects.");return;}
    setSaving("new");setError("");
    try{
      const tok=await getToken();
      const p=await Graph.upsertProject(tok,{...newForm,isActive:true,teamsGroupId:"",teamsGroupName:""});
      setProjects(ps=>[...ps,p]);
      setAddOpen(false);setNewForm({customerName:"",jobNumber:"",projectName:"",projectType:"customer",isQuote:false});
      setSuccess("Project added.");setTimeout(()=>setSuccess(""),3000);
    }catch(e){setError("Add failed: "+e.message);}
    finally{setSaving(null);}
  };

  const startEditProj=(p)=>{setEditingProj(p.spId);setEditProjForm({customerName:p.customerName||"",jobNumber:p.jobNumber||"",projectName:p.projectName||"",isQuote:p.isQuote||false});};
  const saveEditProj=async(p)=>{
    setSaving(p.spId+"-e");setError("");
    try{
      const tok=await getToken();
      const updated={...p,...editProjForm};
      await Graph.upsertProject(tok,updated);
      setProjects(ps=>ps.map(x=>x.spId===p.spId?updated:x));
      setEditingProj(null);
    }catch(e){setError("Save failed: "+e.message);}
    finally{setSaving(null);}
  };

  const customerProjects=projects.filter(p=>p.projectType==="customer").sort((a,z)=>(a.customerName||"").localeCompare(z.customerName||""));
  const internalProjects=projects.filter(p=>p.projectType==="internal").sort((a,z)=>a.projectName.localeCompare(z.projectName));
  const productProjects=projects.filter(p=>p.projectType==="product").sort((a,z)=>a.projectName.localeCompare(z.projectName));

  return(
    <div className="max-w-4xl space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-base font-semibold flex items-center gap-2"><FolderOpen size={16} style={{color:BLUE}}/>Projects</h2>
          <p className="text-xs text-slate-400 mt-0.5">{customerProjects.filter(p=>p.isActive).length} active customer · {internalProjects.length} internal categories</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={loadProjects} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-600"><RefreshCw size={11}/>Refresh</button>
          <button onClick={syncTeams} disabled={syncing} className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-white rounded-lg font-semibold disabled:opacity-50" style={{background:"#0078D4"}}>{syncing?<Spin/>:<Cloud size={11}/>}Sync Teams</button>
          <button onClick={()=>setAddOpen(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-white rounded-lg font-semibold" style={{background:BLUE}}><Plus size={11}/>Add Project</button>
        </div>
      </div>

      {error&&<div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 flex items-center gap-2"><AlertCircle size={14}/>{error}</div>}
      {success&&<div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-sm text-emerald-700 flex items-center gap-2"><Check size={14}/>{success}</div>}

      {syncMatches&&(
        <div className="bg-white border border-blue-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 bg-blue-50 border-b border-blue-200 flex items-center justify-between">
            <p className="text-sm font-semibold text-blue-800 flex items-center gap-2"><Cloud size={13}/>{syncMatches.length} Teams groups matched</p>
            <button onClick={()=>setSyncMatches(null)} className="text-xs text-slate-500 hover:text-slate-700">Dismiss</button>
          </div>
          <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
            {syncMatches.length===0&&<p className="text-sm text-slate-400 text-center py-8">No Teams groups matched the project format.</p>}
            {syncMatches.map(m=>(
              <div key={m.teamsGroupId} className="flex items-center gap-3 px-4 py-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-700">{m.jobNumber} – {m.projectName}</p>
                  <p className="text-xs text-slate-400">{m.customerName} · {m.teamsGroupName}</p>
                </div>
                {m.already
                  ?<span className="text-xs bg-emerald-50 text-emerald-600 border border-emerald-200 px-2 py-1 rounded-full">Already imported</span>
                  :<button onClick={()=>importProject(m)} disabled={saving===m.teamsGroupId} className="text-xs px-3 py-1.5 text-white rounded-lg font-semibold disabled:opacity-50" style={{background:BLUE}}>Import</button>}
              </div>
            ))}
          </div>
        </div>
      )}

      {addOpen&&(
        <div className="bg-white border border-blue-200 rounded-xl p-5 shadow-sm space-y-4">
          <p className="text-sm font-semibold flex items-center gap-2" style={{color:BLUE}}><Plus size={14}/>Add Project</p>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-semibold text-slate-500 mb-1">Type</label>
              <Sel value={newForm.projectType} onChange={e=>setNewForm(p=>({...p,projectType:e.target.value,customerName:"",jobNumber:""}))}>
                <option value="customer">Customer Project</option><option value="internal">Internal</option><option value="product">Product</option>
              </Sel></div>
            {newForm.projectType==="customer"&&<><div><label className="block text-xs font-semibold text-slate-500 mb-1">Customer Name</label><Inp value={newForm.customerName} onChange={e=>setNewForm(p=>({...p,customerName:e.target.value}))} placeholder="Acme Corp"/></div>
            <div><label className="block text-xs font-semibold text-slate-500 mb-1">Job Number *</label><Inp value={newForm.jobNumber} onChange={e=>setNewForm(p=>({...p,jobNumber:e.target.value.toUpperCase()}))} placeholder="1234-5678"/></div></>}
            <div className={newForm.projectType==="customer"?"":"col-span-2"}><label className="block text-xs font-semibold text-slate-500 mb-1">{newForm.projectType==="product"?"Product Name *":"Project Name *"}</label><Inp value={newForm.projectName} onChange={e=>setNewForm(p=>({...p,projectName:e.target.value}))} placeholder={newForm.projectType==="product"?"e.g. ABOT":"Robot Arm Assembly"}/></div>
            {newForm.projectType==="product"&&<div><label className="block text-xs font-semibold text-slate-500 mb-1">Current Version</label><Inp value={newForm.currentVersion||""} onChange={e=>setNewForm(p=>({...p,currentVersion:e.target.value}))} placeholder="e.g. 2.1.4"/></div>}
            {newForm.projectType==="customer"&&<div className="flex items-center gap-2 pt-5"><input type="checkbox" id="isQuote" checked={newForm.isQuote} onChange={e=>setNewForm(p=>({...p,isQuote:e.target.checked}))} className="rounded"/><label htmlFor="isQuote" className="text-sm text-slate-600">This is a quote</label></div>}
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={()=>setAddOpen(false)} className="px-4 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50">Cancel</button>
            <button onClick={addProject} disabled={saving==="new"} className="px-4 py-2 text-sm text-white rounded-lg font-semibold flex items-center gap-2 disabled:opacity-50" style={{background:BLUE}}>{saving==="new"&&<Spin/>}Add</button>
          </div>
        </div>
      )}

      {loading?<div className="flex items-center justify-center py-16"><Spin/></div>:(
        <div className="space-y-4">
          {customerProjects.length>0&&(
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100"><p className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-1.5"><Building2 size={10}/>Customer Projects ({customerProjects.length})</p></div>
              <table className="w-full text-sm">
                <thead><tr className="border-b border-slate-100"><th className="text-left px-4 py-2 text-xs font-semibold text-slate-400">Customer</th><th className="text-left px-4 py-2 text-xs font-semibold text-slate-400">Job #</th><th className="text-left px-4 py-2 text-xs font-semibold text-slate-400">Project</th><th className="text-left px-4 py-2 text-xs font-semibold text-slate-400">Quote</th><th className="text-left px-4 py-2 text-xs font-semibold text-slate-400">Status</th><th className="px-4 py-2"/></tr></thead>
                <tbody>
                  {customerProjects.map(p=>(
                    editingProj===p.spId?(
                      <tr key={p.spId} className="border-b border-blue-100 bg-blue-50">
                        <td className="px-2 py-2"><Inp value={editProjForm.customerName} onChange={e=>setEditProjForm(f=>({...f,customerName:e.target.value}))} placeholder="Customer"/></td>
                        <td className="px-2 py-2"><Inp value={editProjForm.jobNumber} onChange={e=>setEditProjForm(f=>({...f,jobNumber:e.target.value.toUpperCase()}))} placeholder="Job #"/></td>
                        <td className="px-2 py-2"><Inp value={editProjForm.projectName} onChange={e=>setEditProjForm(f=>({...f,projectName:e.target.value}))} placeholder="Project name"/></td>
                        <td className="px-2 py-2"><input type="checkbox" checked={editProjForm.isQuote} onChange={e=>setEditProjForm(f=>({...f,isQuote:e.target.checked}))} className="rounded"/></td>
                        <td/>
                        <td className="px-2 py-2 flex gap-1.5">
                          <button onClick={()=>saveEditProj(p)} disabled={saving===p.spId+"-e"} className="text-xs px-2.5 py-1 text-white rounded-lg font-semibold disabled:opacity-50" style={{background:BLUE}}>{saving===p.spId+"-e"?<Spin/>:"Save"}</button>
                          <button onClick={()=>setEditingProj(null)} className="text-xs px-2.5 py-1 border border-slate-200 rounded-lg hover:bg-slate-100">Cancel</button>
                        </td>
                      </tr>
                    ):(
                    <tr key={p.spId} className={"border-b border-slate-50 "+(p.isActive?"":"opacity-50")}>
                      <td className="px-4 py-2.5 text-sm text-slate-700">{p.customerName}</td>
                      <td className="px-4 py-2.5"><span className="text-xs font-mono font-semibold text-slate-600">{p.jobNumber}</span></td>
                      <td className="px-4 py-2.5 font-medium">{p.projectName}{p.teamsGroupId&&<span className="ml-1.5 text-xs text-blue-400" title="Synced from Teams">⬡</span>}</td>
                      <td className="px-4 py-2.5">
                        <button onClick={()=>toggleQuote(p)} disabled={saving===p.spId+"-q"} className={"text-xs px-2 py-0.5 rounded-full border font-medium "+(p.isQuote?"bg-amber-50 text-amber-600 border-amber-200 hover:bg-amber-100":"bg-slate-50 text-slate-400 border-slate-200 hover:bg-slate-100")}>
                          {p.isQuote?"Quote":"Project"}
                        </button>
                      </td>
                      <td className="px-4 py-2.5">
                        <button onClick={()=>toggleActive(p)} disabled={saving===p.spId} className={"text-xs px-2.5 py-1 rounded-full border font-medium "+(p.isActive?"text-emerald-600 bg-emerald-50 border-emerald-200 hover:bg-emerald-100":"text-slate-400 bg-slate-50 border-slate-200 hover:bg-slate-100")}>
                          {saving===p.spId?<Spin/>:p.isActive?"Active":"Inactive"}
                        </button>
                      </td>
                      <td className="px-4 py-2.5">
                        <button onClick={()=>startEditProj(p)} className="text-xs px-2.5 py-1 border border-slate-200 rounded-lg hover:bg-slate-100 text-slate-500">Edit</button>
                      </td>
                    </tr>
                    )
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {productProjects.length>0&&(
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100"><p className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-1.5"><Bug size={10}/>Products ({productProjects.length})</p></div>
              <div className="divide-y divide-slate-50">
                {productProjects.map(p=>(
                  <div key={p.spId} className={"flex items-center justify-between px-4 py-2.5 "+(p.isActive?"":"opacity-50")}>
                    <div>
                      <span className="text-sm font-medium text-slate-700">{p.projectName}</span>
                      {p.currentVersion&&<span className="ml-2 text-xs font-mono text-slate-400">v{p.currentVersion}</span>}
                    </div>
                    <button onClick={()=>toggleActive(p)} disabled={saving===p.spId} className={"text-xs px-2.5 py-1 rounded-full border font-medium "+(p.isActive?"text-emerald-600 bg-emerald-50 border-emerald-200 hover:bg-emerald-100":"text-slate-400 bg-slate-50 border-slate-200 hover:bg-slate-100")}>
                      {saving===p.spId?<Spin/>:p.isActive?"Active":"Inactive"}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {internalProjects.length>0&&(
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100"><p className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-1.5"><Tag size={10}/>Internal Categories ({internalProjects.length})</p></div>
              <div className="divide-y divide-slate-50">
                {internalProjects.map(p=>(
                  <div key={p.spId} className={"flex items-center justify-between px-4 py-2.5 "+(p.isActive?"":"opacity-50")}>
                    <span className="text-sm font-medium text-slate-700">{p.projectName}</span>
                    <button onClick={()=>toggleActive(p)} disabled={saving===p.spId} className={"text-xs px-2.5 py-1 rounded-full border font-medium "+(p.isActive?"text-emerald-600 bg-emerald-50 border-emerald-200 hover:bg-emerald-100":"text-slate-400 bg-slate-50 border-slate-200 hover:bg-slate-100")}>
                      {saving===p.spId?<Spin/>:p.isActive?"Active":"Inactive"}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {customerProjects.length===0&&internalProjects.length===0&&!loading&&(
            <p className="text-sm text-slate-400 text-center py-12">No projects yet. Sync from Teams or add one manually.</p>
          )}
        </div>
      )}
    </div>
  );
}

function ReportsAdmin({cu,getToken}){
  const[items,setItems]=useState([]);
  const[loading,setLoading]=useState(false);
  const[error,setError]=useState("");
  const[filterAction,setFilterAction]=useState("");
  const[filterUser,setFilterUser]=useState("");
  const[fromDate,setFromDate]=useState("");
  const[toDate,setToDate]=useState("");

  const load=async()=>{
    setLoading(true);setError("");
    try{
      const data=await Graph.getFullHistory({action:filterAction||undefined,user:filterUser||undefined,from:fromDate?fromDate+"T00:00:00Z":undefined,to:toDate?toDate+"T23:59:59Z":undefined});
      setItems(data);
    }catch(e){setError("Failed to load: "+e.message);}
    finally{setLoading(false);}
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(()=>{load();},[]);

  const ACTIONS=["created","status_changed","priority_changed","assigned","accepted","declined","due_date_changed","visibility_changed","project_changed","standup_toggled","standup_note","recurrence_changed","deleted","notes_changed"];
  const ACTION_COLOR={created:"#059669",deleted:"#DC2626",accepted:"#059669",declined:"#DC2626",status_changed:"#2563EB",assigned:"#7C3AED",standup_note:"#D97706"};

  const uniqueUsers=[...new Set(items.map(i=>i.changedBy).filter(Boolean))].sort();

  return(
    <div className="max-w-5xl space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-base font-semibold flex items-center gap-2"><History size={16} style={{color:BLUE}}/>Task Activity Report</h2>
          <p className="text-xs text-slate-400 mt-0.5">{items.length} entries loaded</p>
        </div>
        <button onClick={load} disabled={loading} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-600 disabled:opacity-50">{loading?<Spin/>:<RefreshCw size={11}/>}Run Report</button>
      </div>

      {error&&<div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 flex items-center gap-2"><AlertCircle size={14}/>{error}</div>}

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1">Action</label>
          <select value={filterAction} onChange={e=>setFilterAction(e.target.value)} className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none">
            <option value="">All Actions</option>
            {ACTIONS.map(a=><option key={a} value={a}>{a.replace(/_/g," ")}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1">User</label>
          <select value={filterUser} onChange={e=>setFilterUser(e.target.value)} className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none">
            <option value="">All Users</option>
            {uniqueUsers.map(u=><option key={u} value={u}>{u.split("@")[0]}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1">From</label>
          <input type="date" value={fromDate} onChange={e=>setFromDate(e.target.value)} className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none"/>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1">To</label>
          <input type="date" value={toDate} onChange={e=>setToDate(e.target.value)} className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none"/>
        </div>
      </div>

      {/* Summary counts */}
      {items.length>0&&(
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {[["Created",items.filter(i=>i.action==="created").length,"#059669"],["Completed",items.filter(i=>i.action==="status_changed"&&i.newValue==="completed").length,"#2563EB"],["Assigned",items.filter(i=>i.action==="assigned").length,"#7C3AED"],["Accepted",items.filter(i=>i.action==="accepted").length,"#059669"],["Declined",items.filter(i=>i.action==="declined").length,"#DC2626"],["Deleted",items.filter(i=>i.action==="deleted").length,"#94A3B8"]].map(([lb,count,color])=>(
            <div key={lb} className="bg-white border border-slate-200 rounded-xl px-3 py-3 text-center">
              <p className="text-xl font-bold" style={{color}}>{count}</p>
              <p className="text-xs text-slate-500 mt-0.5">{lb}</p>
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      {items.length===0&&!loading&&<p className="text-sm text-slate-400 text-center py-10">No activity found. Adjust filters and run report.</p>}
      {items.length>0&&(
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="border-b border-slate-100 bg-slate-50">
                <th className="text-left px-4 py-2.5 text-slate-500 font-semibold">Date / Time</th>
                <th className="text-left px-4 py-2.5 text-slate-500 font-semibold">User</th>
                <th className="text-left px-4 py-2.5 text-slate-500 font-semibold">Action</th>
                <th className="text-left px-4 py-2.5 text-slate-500 font-semibold">Task</th>
                <th className="text-left px-4 py-2.5 text-slate-500 font-semibold">Details</th>
              </tr></thead>
              <tbody>
                {items.map((a,i)=>{
                  const d=a.changedAt?new Date(a.changedAt):null;
                  const dateStr=d?d.toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})+" "+d.toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"}):"";
                  return(
                    <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                      <td className="px-4 py-2 text-slate-500 whitespace-nowrap">{dateStr}</td>
                      <td className="px-4 py-2 font-medium text-slate-700">{a.changedBy.split("@")[0]}</td>
                      <td className="px-4 py-2"><span className="font-semibold" style={{color:ACTION_COLOR[a.action]||"#64748B"}}>{a.action.replace(/_/g," ")}</span></td>
                      <td className="px-4 py-2 text-slate-600 max-w-xs truncate">{a.taskTitle}</td>
                      <td className="px-4 py-2 text-slate-500 max-w-xs truncate">{a.details}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function FeedbackAdmin({cu,getToken}){
  const[items,setItems]=useState([]);
  const[loading,setLoading]=useState(true);
  const[error,setError]=useState("");
  const[filterType,setFilterType]=useState("");
  const[filterStatus,setFilterStatus]=useState("open");
  const[expanded,setExpanded]=useState(null);
  const[editing,setEditing]=useState({}); // spId -> {status,assignedTo,resolutionNotes}
  const[saving,setSaving]=useState(null);
  const[saveOk,setSaveOk]=useState(null);
  const[reply,setReply]=useState({}); // spId -> {open,text,sending,sent}
  const[resEmail,setResEmail]=useState({}); // spId -> {sending,sent,error}

  const load=async()=>{
    setLoading(true);setError("");
    try{const data=await Graph.getFeedback();setItems(data);}
    catch(e){setError("Failed to load: "+e.message);}
    finally{setLoading(false);}
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(()=>{load();},[]);

  const STATUSES=["new","in_review","resolved","wont_fix","closed"];
  const TYPE_META={bug:{lb:"Bug",Icon:Bug,color:"#DC2626",bg:"#FEF2F2"},enhancement:{lb:"Enhancement",Icon:Lightbulb,color:"#D97706",bg:"#FFFBEB"},question:{lb:"Question",Icon:HelpCircle,color:"#2563EB",bg:"#EFF6FF"}};
  const STATUS_COLOR={new:"#94A3B8",in_review:"#2563EB",resolved:"#059669",wont_fix:"#DC2626",closed:"#64748B"};

  const visible=items.filter(i=>{
    if(filterType&&i.feedbackType!==filterType)return false;
    if(filterStatus==="open"&&["wont_fix","closed"].includes(i.status))return false;
    if(filterStatus==="closed"&&!["wont_fix","closed"].includes(i.status))return false;
    return true;
  });

  const startEdit=item=>setEditing(e=>({...e,[item.spId]:{status:item.status,assignedTo:item.assignedTo||"",resolutionNotes:item.resolutionNotes||""}}));
  const updEdit=(spId,k,v)=>setEditing(e=>({...e,[spId]:{...e[spId],[k]:v}}));

  const save=async(item)=>{
    const ed=editing[item.spId];if(!ed)return;
    setSaving(item.spId);
    try{
      const updated={...item,...ed};
      await Graph.updateFeedback(updated);
      setItems(arr=>arr.map(x=>x.spId===item.spId?updated:x));
      setEditing(e=>{const n={...e};delete n[item.spId];return n;});
      setSaveOk(item.spId);setTimeout(()=>setSaveOk(null),2000);
      // If item would now be filtered out, switch to All view so it stays visible
      if(filterStatus==="open"&&["wont_fix","closed"].includes(ed.status)){
        setFilterStatus("");
      }else if(filterStatus==="closed"&&!["wont_fix","closed"].includes(ed.status)){
        setFilterStatus("");
      }
    }catch(e){setError("Save failed: "+e.message);}
    finally{setSaving(null);}
  };

  const sendReply=async(item,cu)=>{
    const text=(reply[item.spId]?.text||"").trim();
    if(!text)return;
    setReply(r=>({...r,[item.spId]:{...r[item.spId],sending:true}}));
    try{
      const senderName=cu?.name||dispName(cu?.email,[])||"Admin";
      const merged={...item,...(editing[item.spId]||{})};
      await Graph.sendFeedbackReplyEmail(merged,text,senderName);
      // Save reply to history in SharePoint
      const entry={ts:new Date().toISOString(),text,by:cu.email,byName:senderName};
      let newHistory=[...(item.replyHistory||[]),entry];
      try{newHistory=await Graph.addFeedbackReply(merged,entry);}
      catch(e){console.warn("Reply history save failed:",e.message);}
      setItems(arr=>arr.map(x=>x.spId===item.spId?{...x,replyHistory:newHistory}:x));
      setReply(r=>({...r,[item.spId]:{open:false,text:"",sending:false,sent:true}}));
      setTimeout(()=>setReply(r=>({...r,[item.spId]:{...r[item.spId],sent:false}})),3000);
    }catch(e){setReply(r=>({...r,[item.spId]:{...r[item.spId],sending:false}}));setError("Reply failed: "+e.message);}
  };

  const sendResolutionEmail=async(item,cu)=>{
    setResEmail(r=>({...r,[item.spId]:{sending:true,sent:false,error:""}}));
    try{
      const senderName=cu?.name||cu?.email?.split("@")[0]||"Admin";
      const merged={...item,...(editing[item.spId]||{})};
      await Graph.sendResolutionEmail(merged,senderName);
      setResEmail(r=>({...r,[item.spId]:{sending:false,sent:true,error:""}}));
      setTimeout(()=>setResEmail(r=>({...r,[item.spId]:{...r[item.spId],sent:false}})),4000);
    }catch(e){setResEmail(r=>({...r,[item.spId]:{sending:false,sent:false,error:e.message}}));}
  };

  if(loading)return<div className="flex items-center justify-center py-16"><Spin/></div>;

  return(
    <div className="max-w-4xl space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-base font-semibold flex items-center gap-2"><Bug size={16} style={{color:BLUE}}/>Feedback &amp; Bug Reports</h2>
          <p className="text-xs text-slate-400 mt-0.5">{items.filter(i=>i.status==="new").length} new · {items.filter(i=>i.status==="in_review").length} in review · {items.length} total</p>
        </div>
        <button onClick={load} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-600"><RefreshCw size={11}/>Refresh</button>
      </div>

      {error&&<div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 flex items-center gap-2"><AlertCircle size={14}/>{error}</div>}

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        <div className="flex rounded-lg border border-slate-200 overflow-hidden text-xs font-semibold">
          {[["open","Open"],["closed","Closed"],["","All"]].map(([v,lb])=>(
            <button key={v} onClick={()=>setFilterStatus(v)} className="px-3 py-1.5 transition-colors" style={filterStatus===v?{background:BLUE,color:"white"}:{color:"#64748B"}}>{lb}</button>
          ))}
        </div>
        <div className="flex rounded-lg border border-slate-200 overflow-hidden text-xs font-semibold">
          {[["","All Types"],["bug","Bugs"],["enhancement","Enhancements"],["question","Questions"]].map(([v,lb])=>(
            <button key={v} onClick={()=>setFilterType(v)} className="px-3 py-1.5 transition-colors" style={filterType===v?{background:"#1E293B",color:"white"}:{color:"#64748B"}}>{lb}</button>
          ))}
        </div>
      </div>

      {visible.length===0&&<p className="text-sm text-slate-400 text-center py-10">No feedback matching current filters.</p>}

      <div className="space-y-2">
        {visible.map(item=>{
          const meta=TYPE_META[item.feedbackType]||TYPE_META.enhancement;
          const TypeIcon=meta.Icon;
          const isExp=expanded===item.spId;
          const ed=editing[item.spId];
          const createdDate=item.createdAt?new Date(item.createdAt).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}):"";
          return(
            <div key={item.spId} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              {/* Header row */}
              <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50" onClick={()=>{setExpanded(e=>e===item.spId?null:item.spId);if(!editing[item.spId])startEdit(item);}}>
                <span className="p-1.5 rounded-lg shrink-0" style={{background:meta.bg}}><TypeIcon size={13} style={{color:meta.color}}/></span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">{item.title}</p>
                  <p className="text-xs text-slate-400">{dispName(item.submittedBy,[])} · {createdDate}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {item.priority==="high"&&<span className="text-xs font-semibold text-red-500 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full">High</span>}
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{background:STATUS_COLOR[item.status]+"22",color:STATUS_COLOR[item.status]}}>{item.status.replace("_"," ")}</span>
                  {isExp?<ChevronUp size={14} className="text-slate-400"/>:<ChevronDown size={14} className="text-slate-400"/>}
                </div>
              </div>

              {/* Expanded detail */}
              {isExp&&(
                <div className="border-t border-slate-100 px-4 py-4 space-y-4 bg-slate-50">
                  <div>
                    <p className="text-xs font-semibold text-slate-500 mb-1">Description</p>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{item.description}</p>
                  </div>

                  {item.screenshotUrls?.length>0&&(
                    <div>
                      <p className="text-xs font-semibold text-slate-500 mb-2">Screenshots</p>
                      <div className="flex gap-2 flex-wrap">
                        {item.screenshotUrls.map((url,i)=>(
                          <a key={i} href={url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-blue-600 hover:underline border border-blue-200 bg-blue-50 rounded-lg px-2 py-1">
                            <ExternalLink size={10}/>Screenshot {i+1}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Edit controls */}
                  {ed&&(
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-slate-500 mb-1">Status</label>
                        <select value={ed.status} onChange={e=>updEdit(item.spId,"status",e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none">
                          {STATUSES.map(s=><option key={s} value={s}>{s.replace("_"," ")}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-500 mb-1">Assigned To</label>
                        <Inp value={ed.assignedTo} onChange={e=>updEdit(item.spId,"assignedTo",e.target.value)} placeholder="admin@dacrobotics.com"/>
                      </div>
                      <div className="col-span-2">
                        <label className="block text-xs font-semibold text-slate-500 mb-1">Resolution Notes</label>
                        <textarea value={ed.resolutionNotes} onChange={e=>updEdit(item.spId,"resolutionNotes",e.target.value)} rows={2} placeholder="Steps taken, fix deployed, workaround…" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none resize-none bg-white"/>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      {/* Reply button */}
                      {reply[item.spId]?.sent
                        ?<span className="text-xs text-emerald-600 flex items-center gap-1"><Check size={12}/>Reply sent</span>
                        :<button onClick={()=>setReply(r=>({...r,[item.spId]:{...r[item.spId],open:!r[item.spId]?.open,text:r[item.spId]?.text||""}}))} className="flex items-center gap-1.5 px-3 py-2 text-xs border border-slate-200 rounded-lg hover:bg-slate-100 text-slate-600"><Mail size={11}/>Reply to Reporter</button>
                      }
                      {/* Send Resolution Email — only when resolved/wont_fix/closed */}
                      {["resolved","wont_fix","closed"].includes(ed?.status||item.status)&&(
                        resEmail[item.spId]?.sent
                          ?<span className="text-xs text-emerald-600 flex items-center gap-1"><Check size={12}/>Resolution email sent</span>
                          :<button onClick={()=>sendResolutionEmail(item,cu)} disabled={resEmail[item.spId]?.sending} className="flex items-center gap-1.5 px-3 py-2 text-xs border border-emerald-200 rounded-lg hover:bg-emerald-50 text-emerald-700 font-semibold disabled:opacity-50">{resEmail[item.spId]?.sending?<Spin/>:<Send size={11}/>}Send Resolution Email</button>
                      )}
                      {resEmail[item.spId]?.error&&<span className="text-xs text-red-500">{resEmail[item.spId].error}</span>}
                    </div>
                    <div>
                      {saveOk===item.spId
                        ?<span className="text-xs text-emerald-600 flex items-center gap-1"><Check size={12}/>Saved</span>
                        :<button onClick={()=>save(item)} disabled={saving===item.spId} className="flex items-center gap-1.5 px-4 py-2 text-xs text-white font-semibold rounded-lg disabled:opacity-50" style={{background:BLUE}}>{saving===item.spId&&<Spin/>}<Save size={11}/>Save Changes</button>
                      }
                    </div>
                  </div>
                  {/* Inline reply panel */}
                  {reply[item.spId]?.open&&(
                    <div className="mt-3 border border-blue-200 rounded-xl bg-blue-50 p-3">
                      <p className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1"><Mail size={11}/>Reply to {dispName(item.submittedBy,[])}</p>
                      <textarea value={reply[item.spId]?.text||""} onChange={e=>setReply(r=>({...r,[item.spId]:{...r[item.spId],text:e.target.value}}))} rows={3} placeholder="Type your reply — this will be emailed to the reporter…" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none resize-none bg-white"/>
                      <div className="flex gap-2 mt-2">
                        <button onClick={()=>sendReply(item,cu)} disabled={reply[item.spId]?.sending||!reply[item.spId]?.text?.trim()} className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-white font-semibold rounded-lg disabled:opacity-50" style={{background:BLUE}}>{reply[item.spId]?.sending?<Spin/>:<Send size={11}/>}Send Reply</button>
                        <button onClick={()=>setReply(r=>({...r,[item.spId]:{...r[item.spId],open:false}}))} className="px-3 py-1.5 text-xs border border-slate-200 rounded-lg text-slate-500 hover:bg-slate-100">Cancel</button>
                      </div>
                    </div>
                  )}

                  {/* Reply history */}
                  {item.replyHistory?.length>0&&(
                    <div>
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1"><History size={11}/>Reply History</p>
                      <div className="space-y-2">
                        {[...item.replyHistory].reverse().map((h,i)=>(
                          <div key={i} className="bg-white border border-slate-200 rounded-xl px-3 py-2.5">
                            <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
                              <span className="text-xs font-semibold text-slate-700">{h.byName||dispName(h.by,[])}</span>
                              <span className="text-xs text-slate-400">{new Date(h.ts).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric",hour:"numeric",minute:"2-digit"})}</span>
                            </div>
                            <p className="text-sm text-slate-600 whitespace-pre-wrap leading-relaxed">{h.text}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AdminPage({cu,getToken,projects=[],setProjects,onImpersonate,impersonating=false,impersonatingEmail="",isAdmin=true}){
  const[tab,setTab]=useState(isAdmin?"users":"projects");
  const[users,setUsers]=useState([]);
  const[loading,setLoading]=useState(true);
  const[saving,setSaving]=useState(null);
  const[syncing,setSyncing]=useState(false);
  const[editId,setEditId]=useState(null);
  const[editForm,setEditForm]=useState({});
  const[addOpen,setAddOpen]=useState(false);
  const[newForm,setNewForm]=useState({email:"",displayName:"",role:"User",manager1:"",manager2:"",department:"",jobTitle:"",isActive:true});
  const[error,setError]=useState("");
  const[success,setSuccess]=useState("");
  const[syncResult,setSyncResult]=useState(null);
  const[showInactive,setShowInactive]=useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(()=>{loadUsers();},[]);

  const loadUsers=async()=>{
    setLoading(true);setError("");
    try{
      const tok=await getToken();
      const u=await Graph.getUsers(tok);
      console.log("[AdminPage] getUsers returned",u.length,"users",u.map(x=>({email:x.email,role:x.role,isActive:x.isActive})));
      setUsers(u.sort((a,z)=>(a.displayName||"").localeCompare(z.displayName||"")));
    }
    catch(e){console.error("[AdminPage] loadUsers error",e);setError("Failed to load users: "+e.message);}
    finally{setLoading(false);}
  };

  const startEdit=u=>{setEditId(u.id);setEditForm({role:u.role,manager1:u.manager1||"",manager2:u.manager2||"",department:u.department||"",jobTitle:u.jobTitle||"",isActive:u.isActive});};
  const cancelEdit=()=>{setEditId(null);setEditForm({});};

  const saveEdit=async(u)=>{
    setSaving(u.id);setError("");
    try{
      const tok=await getToken();
      const updated={...u,...editForm};
      const saved=await Graph.upsertUser(tok,updated);
      setUsers(us=>us.map(x=>x.id===u.id?saved:x));
      setEditId(null);setSuccess("User updated.");setTimeout(()=>setSuccess(""),3000);
    }catch(e){setError("Save failed: "+e.message);}
    finally{setSaving(null);}
  };

  const addUser=async()=>{
    if(!newForm.email||!newForm.displayName){setError("Email and name are required.");return;}
    setSaving("new");setError("");
    try{
      const tok=await getToken();
      const saved=await Graph.upsertUser(tok,{...newForm,email:newForm.email.toLowerCase()});
      setUsers(us=>[...us,saved].sort((a,z)=>a.displayName.localeCompare(z.displayName)));
      setAddOpen(false);setNewForm({email:"",displayName:"",role:"User",manager1:"",manager2:"",department:"",jobTitle:"",isActive:true});
      setSuccess("User added.");setTimeout(()=>setSuccess(""),3000);
    }catch(e){setError("Add failed: "+e.message);}
    finally{setSaving(null);}
  };

  const toggleActive=async(u)=>{
    setSaving(u.id);
    try{const tok=await getToken();const upd={...u,isActive:!u.isActive};const saved=await Graph.upsertUser(tok,upd);setUsers(us=>us.map(x=>x.id===u.id?saved:x));}
    catch(e){setError("Update failed: "+e.message);}
    finally{setSaving(null);}
  };

  const normalizeEmail=e=>(e||"").toLowerCase().replace("@dac-us.com","@dacrobotics.com");

  const syncFromM365=async()=>{
    setSyncing(true);setError("");setSyncResult(null);
    try{
      const tok=await getToken();
      const[m365Users,existing]=await Promise.all([Graph.getM365Users(tok),Graph.getUsers(tok)]);
      // Dedup: for same normalized email, keep highest-role record, deactivate others
      const byNorm={};
      for(const u of existing){
        const key=normalizeEmail(u.email);
        if(!byNorm[key]||byNorm[key].role==="User"){byNorm[key]=u;}
      }
      const keepIds=new Set(Object.values(byNorm).map(u=>u.id));
      let dupes=0;
      for(const u of existing){
        if(!keepIds.has(u.id)&&u.isActive){await Graph.upsertUser(tok,{...u,isActive:false});dupes++;}
      }
      // Upsert M365 users
      let added=0,updated=0;
      for(const m365u of m365Users){
        const ex=byNorm[normalizeEmail(m365u.email)];
        if(ex){
          await Graph.upsertUser(tok,{...ex,displayName:m365u.displayName,jobTitle:m365u.jobTitle,department:m365u.department,isActive:true});
          updated++;
        }else{
          await Graph.upsertUser(tok,m365u);added++;
        }
      }
      const refreshed=await Graph.getUsers(tok);
      setUsers(refreshed.sort((a,z)=>a.displayName.localeCompare(z.displayName)));
      setSyncResult({added,updated,dupes,total:m365Users.length});
    }catch(e){setError("Sync failed: "+e.message);}
    finally{setSyncing(false);}
  };

  const deactivateNonM365=async()=>{
    if(!window.confirm("Mark all users NOT found in M365 as Inactive?"))return;
    setSyncing(true);setError("");
    try{
      const tok=await getToken();
      const[m365Users,existing]=await Promise.all([Graph.getM365Users(tok),Graph.getUsers(tok)]);
      const m365Emails=new Set(m365Users.map(u=>normalizeEmail(u.email)));
      let deactivated=0;
      for(const u of existing){
        if(u.isActive&&!m365Emails.has(normalizeEmail(u.email))){
          await Graph.upsertUser(tok,{...u,isActive:false});deactivated++;
        }
      }
      const refreshed=await Graph.getUsers(tok);
      setUsers(refreshed.sort((a,z)=>a.displayName.localeCompare(z.displayName)));
      setSyncResult({deactivated,total:existing.length});
    }catch(e){setError("Cleanup failed: "+e.message);}
    finally{setSyncing(false);}
  };

  // Test accounts removed — no longer needed

  const activeUsers=users.filter(u=>u.isActive);
  const inactiveUsers=users.filter(u=>!u.isActive);
  const visibleUsers=users.filter(u=>showInactive||u.isActive);

  const allActiveUsers=users.filter(u=>u.isActive).sort((a,z)=>(a.displayName||"").localeCompare(z.displayName||""));
  const MgrSelect=({value,onChange})=>(
    <div>
      {allActiveUsers.length===0&&<p className="text-xs text-red-500 mb-1">No users loaded — click Refresh above first</p>}
      <select value={value||""} onChange={e=>onChange(e.target.value)} className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none">
        <option value="">-- None --</option>
        {allActiveUsers.map(m=><option key={m.id} value={m.email}>{m.displayName||m.email} ({m.role||"User"})</option>)}
      </select>
    </div>
  );

  return(
    <div className="space-y-5">
      <div className="flex gap-1 border-b border-slate-200">
        {[...(isAdmin?[{id:"users",label:"User Management",Icon:Shield}]:[]),{id:"projects",label:"Projects",Icon:FolderOpen},{id:"notifs",label:"Notifications",Icon:Bell},{id:"feedback",label:"Feedback",Icon:Bug},{id:"reports",label:"Reports",Icon:History}].map(({id,label,Icon})=>(
          <button key={id} onClick={()=>setTab(id)} className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px" style={tab===id?{borderColor:BLUE,color:BLUE,background:BLUE+"11"}:{borderColor:"transparent",color:"#94A3B8"}}>
            <Icon size={14}/>{label}
          </button>
        ))}
      </div>

      {tab==="notifs"&&<NotifSettings cu={cu} getToken={getToken}/>}
      {tab==="projects"&&<ProjectsAdmin cu={cu} getToken={getToken} projects={projects} setProjects={setProjects}/>}
      {tab==="feedback"&&<FeedbackAdmin cu={cu} getToken={getToken}/>}
      {tab==="reports"&&<ReportsAdmin cu={cu} getToken={getToken}/>}

      {tab==="users"&&<div className="max-w-4xl space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h2 className="text-base font-semibold flex items-center gap-2"><Shield size={16} style={{color:BLUE}}/>User Management</h2>
            <p className="text-xs text-slate-400 mt-0.5">{activeUsers.length} active  {inactiveUsers.length} inactive</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={loadUsers} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-600"><RefreshCw size={11}/>Refresh</button>
            <button onClick={()=>setShowInactive(v=>!v)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-600">{showInactive?"Hide Inactive":"Show Inactive"}</button>
            <button onClick={deactivateNonM365} disabled={syncing} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-red-200 text-red-600 rounded-lg font-semibold hover:bg-red-50 disabled:opacity-50"><UserMinus size={11}/>Cleanup</button>
            <button onClick={syncFromM365} disabled={syncing} className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-white rounded-lg font-semibold disabled:opacity-50" style={{background:"#0078D4"}}>{syncing?<Spin/>:<Cloud size={11}/>}Sync M365</button>
            <button onClick={()=>setAddOpen(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-white rounded-lg font-semibold" style={{background:BLUE}}><UserPlus size={11}/>Add User</button>
          </div>
        </div>

        {error&&<div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 flex items-center gap-2"><AlertCircle size={14}/>{error}</div>}
        {success&&<div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-sm text-emerald-700 flex items-center gap-2"><Check size={14}/>{success}</div>}
        {syncResult&&<div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-blue-700 flex items-center gap-2"><Download size={14}/>
          <span>{syncResult.deactivated!=null
            ?"Cleanup done -- "+syncResult.deactivated+" users deactivated."
            :"Synced "+syncResult.total+" from M365 -- "+syncResult.added+" added, "+syncResult.updated+" updated"+(syncResult.dupes>0?", "+syncResult.dupes+" duplicates removed":"")+"."
          }</span>
        </div>}

        {addOpen&&(
          <div className="bg-white border border-blue-200 rounded-xl p-5 shadow-sm space-y-4">
            <p className="text-sm font-semibold flex items-center gap-2" style={{color:BLUE}}><UserPlus size={14}/>Add New User</p>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-xs font-semibold text-slate-500 mb-1">Email *</label><Inp type="email" value={newForm.email} onChange={e=>setNewForm(p=>({...p,email:e.target.value}))} placeholder="user@dacrobotics.com"/></div>
              <div><label className="block text-xs font-semibold text-slate-500 mb-1">Display Name *</label><Inp value={newForm.displayName} onChange={e=>setNewForm(p=>({...p,displayName:e.target.value}))} placeholder="First Last"/></div>
              <div><label className="block text-xs font-semibold text-slate-500 mb-1">Role</label><Sel value={newForm.role} onChange={e=>setNewForm(p=>({...p,role:e.target.value}))}><option>Admin</option><option>Manager</option><option>User</option></Sel></div>
              <div><label className="block text-xs font-semibold text-slate-500 mb-1">Job Title</label><Inp value={newForm.jobTitle} onChange={e=>setNewForm(p=>({...p,jobTitle:e.target.value}))} placeholder="Engineer"/></div>
              <div><label className="block text-xs font-semibold text-slate-500 mb-1">Department</label><Inp value={newForm.department} onChange={e=>setNewForm(p=>({...p,department:e.target.value}))} placeholder="Engineering"/></div>
              <div><label className="block text-xs font-semibold text-slate-500 mb-1">Manager 1</label><MgrSelect value={newForm.manager1} onChange={v=>setNewForm(p=>({...p,manager1:v}))}/></div>
              <div><label className="block text-xs font-semibold text-slate-500 mb-1">Manager 2</label><MgrSelect value={newForm.manager2} onChange={v=>setNewForm(p=>({...p,manager2:v}))}/></div>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={()=>setAddOpen(false)} className="px-4 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50">Cancel</button>
              <button onClick={addUser} disabled={saving==="new"} className="px-4 py-2 text-sm text-white rounded-lg font-semibold flex items-center gap-2 disabled:opacity-50" style={{background:BLUE}}>{saving==="new"&&<Spin/>}Add User</button>
            </div>
          </div>
        )}

        {/* ─────────────────────────────────────────────── */}

        {loading?<div className="flex items-center justify-center py-16"><Spin/></div>:(
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-100 bg-slate-50">
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">User</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Role</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 hidden md:table-cell">Manager(s)</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 hidden lg:table-cell">Dept / Title</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Status</th>
                <th className="px-4 py-3"/>
              </tr></thead>
              <tbody>
                {visibleUsers.map(u=>(
                  <tr key={u.id} className={"border-b border-slate-50 "+(u.isActive?"":"opacity-50")}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0" style={{background:BLUE}}>{(u.displayName||u.email)[0].toUpperCase()}</div>
                        <div><p className="font-medium">{u.displayName||u.email.split("@")[0]}</p><p className="text-xs text-slate-400">{u.email}</p></div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {editId===u.id
                        ?<Sel cls="w-28" value={editForm.role} onChange={e=>setEditForm(p=>({...p,role:e.target.value}))}><option>Admin</option><option>Manager</option><option>User</option></Sel>
                        :<RoleBadge role={u.role}/>}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      {editId===u.id?(
                        <div className="space-y-1">
                          <MgrSelect value={editForm.manager1} onChange={v=>setEditForm(p=>({...p,manager1:v}))}/>
                          <MgrSelect value={editForm.manager2} onChange={v=>setEditForm(p=>({...p,manager2:v}))}/>
                        </div>
                      ):(
                        <div className="text-xs text-slate-500 space-y-0.5">
                          {u.manager1&&<p>{u.manager1.split("@")[0]}</p>}
                          {u.manager2&&<p>{u.manager2.split("@")[0]}</p>}
                          {!u.manager1&&!u.manager2&&<span className="text-slate-300">--</span>}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      {editId===u.id?(
                        <div className="space-y-1">
                          <Inp cls="text-xs" value={editForm.department||""} onChange={e=>setEditForm(p=>({...p,department:e.target.value}))} placeholder="Dept"/>
                          <Inp cls="text-xs" value={editForm.jobTitle||""} onChange={e=>setEditForm(p=>({...p,jobTitle:e.target.value}))} placeholder="Title"/>
                        </div>
                      ):(
                        <div className="text-xs text-slate-500">
                          {u.department&&<p className="font-medium">{u.department}</p>}
                          {u.jobTitle&&<p>{u.jobTitle}</p>}
                          {!u.department&&!u.jobTitle&&<span className="text-slate-300">--</span>}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={()=>toggleActive(u)} disabled={saving===u.id} className={"text-xs px-2.5 py-1 rounded-full border font-medium "+(u.isActive?"text-emerald-600 bg-emerald-50 border-emerald-200 hover:bg-emerald-100":"text-slate-400 bg-slate-50 border-slate-200 hover:bg-slate-100")}>
                        {saving===u.id?<Spin/>:u.isActive?"Active":"Inactive"}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {editId===u.id?(
                        <div className="flex gap-2 justify-end">
                          <button onClick={cancelEdit} className="text-xs px-2.5 py-1 border border-slate-200 rounded-lg hover:bg-slate-50">Cancel</button>
                          <button onClick={()=>saveEdit(u)} disabled={saving===u.id} className="text-xs px-2.5 py-1 text-white rounded-lg font-semibold disabled:opacity-50 flex items-center gap-1" style={{background:BLUE}}>{saving===u.id&&<Spin/>}Save</button>
                        </div>
                      ):(
                        <div className="flex gap-1.5 justify-end">
                          <button onClick={()=>startEdit(u)} className="text-xs px-2.5 py-1 border border-slate-200 rounded-lg hover:bg-slate-100 text-slate-600">Edit</button>
                          {onImpersonate&&u.email.toLowerCase()!==cu.email.toLowerCase()&&u.isActive&&(
                            <button onClick={()=>onImpersonate(u)} className={"text-xs px-2.5 py-1 rounded-lg font-semibold flex items-center gap-1 border "+(impersonatingEmail.toLowerCase()===u.email.toLowerCase()?"border-amber-400 bg-amber-100 text-amber-700":"border-amber-200 bg-amber-50 text-amber-600 hover:bg-amber-100")}>
                              <Eye size={10}/>{impersonatingEmail.toLowerCase()===u.email.toLowerCase()?"Acting As":"Act As"}
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>}
    </div>
  );
}
